import React from 'react';
import { Badge, Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { CheckCircle, FileText, Repeat } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { IContratoItem, useFinanceiroContratos } from '../../../../hooks/useFinanceiro';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl, fmtData } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const statusInfo = (status: string | null) => {
  const s = (status || '').toUpperCase();
  if (s === 'ATIVO' || s === 'COMMITTED') return { label: 'Ativo', color: 'green' };
  if (s === 'INATIVO') return { label: 'Inativo', color: 'gray' };
  if (s === 'DRAFT') return { label: 'Rascunho', color: 'gray' };
  if (s === 'CANCELED' || s === 'CANCELLED') return { label: 'Cancelado', color: 'red' };
  return { label: status || '—', color: 'gray' };
};

export const FinanceiroContratosPage = () => {
  const f = useFinanceiroFilters();
  const { data, isError } = useFinanceiroContratos({
    conta_origem: f.apiParams.conta_origem,
    q: f.apiParams.q,
  });

  const itens = data?.itens ?? [];

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Comercial"
          titulo="Contratos / MRR"
          subtitulo="Recorrência dos contratos do Conta Azul (vigentes)"
          icon={<FileText size={24} weight="fill" />}
          cor="#059669"
          info={
            <Text>
              Contratos do Conta Azul e <b>MRR</b> (soma dos contratos ativos). Vigentes — não
              dependem do período selecionado.
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar os contratos.</Text>
          </GlassCard>
        )}

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <KpiCard
            label="MRR (ativos)"
            valor={data?.mrr ?? 0}
            color="#059669"
            icon={<Repeat size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="MRR">
                Receita recorrente mensal = soma do <i>total</i> dos contratos <b>ativos</b> (status
                ATIVO).
              </InfoPopover>
            }
          />
          <KpiCard
            label="Contratos ativos"
            valor={data?.ativos ?? 0}
            format="number"
            color="#16A34A"
            icon={<CheckCircle size={20} weight="duotone" />}
            index={1}
            info={<InfoPopover title="Ativos">Contratos com status COMMITTED.</InfoPopover>}
          />
          <KpiCard
            label="Total de contratos"
            valor={data?.total_contratos ?? 0}
            format="number"
            color="#2563EB"
            icon={<FileText size={20} weight="duotone" />}
            index={2}
            info={<InfoPopover title="Total">Todos os contratos da(s) franquia(s).</InfoPopover>}
          />
        </SimpleGrid>

        <GlassCard intensity="strong" p={{ base: 3, md: 5 }}>
          {itens.length === 0 ? (
            <Text fontSize="sm" color="#94A3B8" textAlign="center" py={6}>
              Nenhum contrato encontrado.
            </Text>
          ) : (
            <Flex direction="column" gap={1}>
              <Flex
                justify="space-between"
                fontSize="10px"
                fontWeight={700}
                color="#94A3B8"
                textTransform="uppercase"
                px={2}
                pb={2}
              >
                <Text>Cliente</Text>
                <Text>Valor</Text>
              </Flex>
              {itens.map((c: IContratoItem) => {
                const st = statusInfo(c.status);
                return (
                  <Flex
                    key={c.id}
                    justify="space-between"
                    align="center"
                    gap={3}
                    px={2}
                    py={2}
                    borderRadius="md"
                    _hover={{ bg: 'rgba(15,23,42,0.03)' }}
                  >
                    <Box minW={0}>
                      <Flex align="center" gap={2}>
                        <Text fontSize="sm" fontWeight={600} color="#0F172A" noOfLines={1}>
                          {c.cliente_nome || '—'}
                        </Text>
                        <Badge colorScheme={st.color} fontSize="9px">
                          {st.label}
                        </Badge>
                      </Flex>
                      {c.proximo_vencimento && (
                        <Text fontSize="11px" color="#94A3B8">
                          próx. venc. {fmtData(c.proximo_vencimento)}
                        </Text>
                      )}
                    </Box>
                    <Text fontSize="sm" fontWeight={700} color="#0F172A" whiteSpace="nowrap">
                      {brl(c.total)}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>
          )}
        </GlassCard>
      </Box>
    </GlassPageBg>
  );
};
